import {useEffect, useEffectEvent, useState} from 'react';
import type {DataLoadStatus} from 'api/types';
import type {Agent, AgentWorkSummary} from 'services/agent/types';
import {searchAgentWorks} from 'api/searchAgentWorks';
import {useGetTokens} from 'services/auth';
import {useCodelists} from 'services/codelists';
import {
  mapWorkHitToWorkSummary,
  useMetadataCodelistIds,
} from 'services/metadata';

export const useAgentWorks = (
  agent: Pick<Agent, 'id' | 'roles'> | undefined,
  page: number,
  size: number,
  doLoad?: boolean,
): {state: DataLoadStatus<AgentWorkSummary[]>; total: number} => {
  const [state, setState] = useState<DataLoadStatus<AgentWorkSummary[]>>({
    status: 'NotLoaded',
  });
  const onSetState = useEffectEvent(setState);
  const [total, setTotal] = useState<number>(0);
  const onSetTotal = useEffectEvent(setTotal);
  const getTokens = useGetTokens();
  const codelistIds = useMetadataCodelistIds();
  const codelists = useCodelists(codelistIds);

  useEffect(() => {
    if (!agent?.id || !doLoad || !agent?.roles) {
      return;
    }

    onSetState(oldState => ({
      ...oldState,
      status: 'Loading',
    }));

    searchAgentWorks(agent.id, page, size, getTokens)
      .then(result => {
        onSetState({
          status: 'Loaded',
          data: result.hits.map(h => ({
            ...mapWorkHitToWorkSummary(h, codelists),
            roles: agent.roles?.find(r => r.workId === h.id)?.roles ?? [],
          })),
        });
        onSetTotal(result.total);
      })
      .catch(error => {
        onSetState({
          status: 'Failed',
          error: error,
        });
      });
  }, [agent?.id, agent?.roles, codelists, doLoad, getTokens, page, size]);

  return {
    state,
    total,
  };
};
